import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { App as AntdApp, ConfigProvider } from 'antd';

import { buildAntdTheme } from '../config/antdTheme';
import {
	DEFAULT_APPEARANCE,
	DEFAULT_THEME,
	THEME_KEYS,
	APPEARANCES,
	buildPalette,
	getTheme,
} from '../config/themes';
import { usePreferences } from './PreferencesContext';

/**
 * Which palette the app is wearing, and which of its two modes.
 *
 * Two preferences drive it: `theme` picks the palette, `appearance` picks
 * light, dark, or system. This provider resolves the pair to a concrete
 * mode, stamps it on <html> for src/css/themes.css to key off, mirrors it
 * into localStorage for the pre-paint script in public/index.html, and
 * builds the matching antd theme.
 *
 * It sits ABOVE PreferencesProvider because it renders the ConfigProvider
 * that antd's App (and therefore the preferences' own error toasts) needs.
 * <ThemeSync> — rendered inside the preferences tree — pushes the saved
 * values back down. Until it does, everyone gets the default.
 */
const ThemeContext = createContext(null);

// At most three messages at once: a burst of failures reads as a few lines,
// never a column of toasts down the page. Stable, because App memoizes on it.
const MESSAGE_CONFIG = { maxCount: 3 };

const STORAGE_THEME = 'keepr.theme';
const STORAGE_APPEARANCE = 'keepr.appearance';
const DARK_QUERY = '(prefers-color-scheme: dark)';

export const normalizeTheme = (value) => (THEME_KEYS.includes(value) ? value : DEFAULT_THEME);
export const normalizeAppearance = (value) => (APPEARANCES.includes(value) ? value : DEFAULT_APPEARANCE);

/** True when the OS is asking for dark. False anywhere matchMedia is absent. */
export function systemPrefersDark() {
	if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
	try {
		return window.matchMedia(DARK_QUERY).matches;
	} catch (err) {
		return false;
	}
}

/**
 * An appearance preference plus the OS setting -> the mode to render.
 * Normalises first: an unrecognised value (a preference written by a newer
 * client, say) has to resolve to light or dark, never pass through as
 * itself — data-mode="system" matches no block in themes.css.
 */
export const resolveMode = (appearance, prefersDark) => {
	const normalized = normalizeAppearance(appearance);
	if (normalized === 'system') return prefersDark ? 'dark' : 'light';
	return normalized;
};

const readStored = (key, normalize) => {
	if (typeof window === 'undefined') return normalize(undefined);
	try {
		return normalize(window.localStorage.getItem(key));
	} catch (err) {
		return normalize(undefined);
	}
};

const writeStored = (key, value) => {
	try {
		window.localStorage.setItem(key, value);
	} catch (err) {
		/* Private windows and blocked site data: the theme still applies,
		   it just will not survive the next first paint. */
	}
};

export function ThemeProvider({ children }) {
	// Seeded from the last visit so the first React paint agrees with what
	// the inline script already put on <html>.
	const [theme, setThemeState] = useState(() => readStored(STORAGE_THEME, normalizeTheme));
	const [appearance, setAppearanceState] = useState(() => readStored(STORAGE_APPEARANCE, normalizeAppearance));
	const [prefersDark, setPrefersDark] = useState(systemPrefersDark);

	// Follow the OS while the appearance is 'system' — and keep listening
	// either way, so switching back to 'system' is already up to date.
	useEffect(() => {
		if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
		const query = window.matchMedia(DARK_QUERY);
		const onChange = (event) => setPrefersDark(event.matches);
		// Safari before 14 only has the deprecated listener API.
		if (query.addEventListener) query.addEventListener('change', onChange);
		else query.addListener(onChange);
		setPrefersDark(query.matches);
		return () => {
			if (query.removeEventListener) query.removeEventListener('change', onChange);
			else query.removeListener(onChange);
		};
	}, []);

	const mode = resolveMode(appearance, prefersDark);

	useEffect(() => {
		if (typeof document === 'undefined') return;
		const root = document.documentElement;
		root.setAttribute('data-theme', theme);
		root.setAttribute('data-mode', mode);
		// Bootstrap 5.3 is still on the page for the legacy forms; it keys
		// its own variables off this attribute.
		root.setAttribute('data-bs-theme', mode);

		// The mobile browser chrome sits directly above the app background,
		// so it wears the ground rather than the brand color.
		const meta = document.querySelector('meta[name="theme-color"]');
		if (meta) meta.setAttribute('content', buildPalette(theme, mode)['--ground']);

		writeStored(STORAGE_THEME, theme);
		writeStored(STORAGE_APPEARANCE, appearance);
	}, [theme, mode, appearance]);

	const setTheme = useCallback((value) => setThemeState(normalizeTheme(value)), []);
	const setAppearance = useCallback((value) => setAppearanceState(normalizeAppearance(value)), []);

	const palette = useMemo(() => buildPalette(theme, mode), [theme, mode]);
	const antdTheme = useMemo(() => buildAntdTheme(theme, mode), [theme, mode]);

	const value = useMemo(() => ({
		theme,
		themeDef: getTheme(theme),
		appearance,
		mode,
		prefersDark,
		palette,
		setTheme,
		setAppearance,
	}), [theme, appearance, mode, prefersDark, palette, setTheme, setAppearance]);

	return (
		<ThemeContext.Provider value={value}>
			<ConfigProvider theme={antdTheme}>
				<AntdApp message={MESSAGE_CONFIG}>{children}</AntdApp>
			</ConfigProvider>
		</ThemeContext.Provider>
	);
}

/**
 * Copies the saved preferences into the theme provider above. A component
 * rather than a hook call inside PreferencesProvider so that the two
 * contexts stay independent: the theme works with no preferences loaded,
 * and preferences work with no theme provider (tests do both).
 */
export function ThemeSync() {
	const themeCtx = useContext(ThemeContext);
	const { prefs } = usePreferences();
	const setTheme = themeCtx && themeCtx.setTheme;
	const setAppearance = themeCtx && themeCtx.setAppearance;

	useEffect(() => {
		if (setTheme) setTheme(prefs.theme);
	}, [setTheme, prefs.theme]);

	useEffect(() => {
		if (setAppearance) setAppearance(prefs.appearance);
	}, [setAppearance, prefs.appearance]);

	return null;
}

export function useTheme() {
	const ctx = useContext(ThemeContext);
	if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
	return ctx;
}

/** Null instead of throwing, for components rendered without the provider. */
export function useOptionalTheme() {
	return useContext(ThemeContext);
}

export default ThemeContext;
